import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../Components/CartContext";
import "./Checkout.css";
import Footer from "../Components/Footer";
import Navbar from "./Components/Navbar";
import { CheckCircle, X, Copy, CopyCheck } from "lucide-react";
import { Elements } from "@stripe/react-stripe-js";
import { getStripe, stripeAppearance } from "../lib/stripe";
import StripePaymentForm from "../Components/StripePaymentForm";
import { apiUrl } from "../api";
import { trackPurchase } from "../lib/analytics";

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, getCartTotal, clearCart } = useCart();

  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: ""
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [copyPopupVisible, setCopyPopupVisible] = useState(false); // now used for icon swap

  // Stripe: the client secret unlocks the PaymentIntent the server priced.
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [serverPricing, setServerPricing] = useState(null);
  const [paymentError, setPaymentError] = useState("");
  // Held in a ref too, so re-pricing reuses one intent instead of opening a new
  // one every time the basket changes.
  const intentIdRef = useRef("");

  useEffect(() => {
    if ((!location.state || !cart.items || cart.items.length === 0) && !orderPlaced) {
      navigate("/cart");
      return;
    }
  }, [location.state, cart.items, navigate, orderPlaced]);

  useEffect(() => {
    // Checkout is guest-only - there are no customer accounts.
    setIsLoadingUserData(false);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Ask the server to price the basket and open a PaymentIntent. The server is
  // the only place prices are trusted - we just display what it returns.
  useEffect(() => {
    const items = cart.items || [];
    if (items.length === 0 || orderPlaced) return;

    let cancelled = false;
    const state = location.state || {};

    (async () => {
      try {
        const res = await fetch(apiUrl('/api/payment/stripe/create-payment-intent'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId: intentIdRef.current || undefined,
            items: items.map((i) => ({
              id: i.id,
              name: i.name,
              variant: i.variant,
              quantity: i.quantity,
            })),
            deliveryType: state.deliveryOption === 'local' ? 'local' : 'pickup',
            postcode: state.pinCode,
            deliveryDetails: {
              pinCode: state.pinCode,
              deliveryArea: state.deliveryArea,
              selectedDate: state.selectedDate,
              selectedTimeSlot: state.selectedTimeSlot,
            },
          }),
        });
        const data = await res.json();
        if (cancelled) return;

        if (!res.ok || !data.success) {
          setPaymentError(data.message || 'Could not start payment. Please try again.');
          return;
        }

        intentIdRef.current = data.data.paymentIntentId;
        setClientSecret(data.data.clientSecret);
        setPaymentIntentId(data.data.paymentIntentId);
        setServerPricing(data.data.pricing);
        setPaymentError('');
      } catch (err) {
        if (!cancelled) {
          setPaymentError('Could not reach the payment service. Please check your connection.');
        }
      }
    })();

    return () => { cancelled = true; };
    // Re-price whenever the basket contents change.
  }, [cart.items, location.state, orderPlaced]);

  // Coming back from a 3-D Secure / wallet redirect: finish the order.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returningIntent = params.get('payment_intent');
    if (!returningIntent) return;

    (async () => {
      try {
        const res = await fetch(apiUrl('/api/payment/stripe/confirm'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId: returningIntent }),
        });
        const data = await res.json();
        if (data.success) {
          setOrderPlaced(true);
          setOrderId(data.data.orderId);
          setShowSuccessModal(true);
          clearCart();
          window.history.replaceState({}, '', '/checkout');
        } else {
          setPaymentError(data.message || 'We could not confirm that payment.');
        }
      } catch {
        setPaymentError('We could not confirm that payment. Please contact us with your email address.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if ((!location.state || !cart.items || cart.items.length === 0) && !orderPlaced) {
    return null;
  }

  const {
    deliveryOption = location.state?.deliveryOption,
    deliveryCharge = location.state?.deliveryCharge,
    selectedDate = location.state?.selectedDate,
    selectedTimeSlot = location.state?.selectedTimeSlot
  } = location.state || {};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const { name, email, phone, address } = customerInfo;
    return name.trim() && email.trim() && phone.trim() && address.trim();
  };

  // The server is the source of truth for money once it has priced the basket.
  const getSubtotal = () => (serverPricing ? serverPricing.subtotal : getCartTotal());
  const getDeliveryCharge = () =>
    serverPricing ? serverPricing.deliveryCharge : (deliveryOption === "local" ? (deliveryCharge || 0) : 0);
  const getTotal = () => (serverPricing ? serverPricing.total : getSubtotal() + getDeliveryCharge());

  /**
   * Runs just before Stripe takes the money: pushes the customer's details onto
   * the existing PaymentIntent so the webhook can build the order from them.
   * Returning false aborts the payment.
   */
  const handleBeforePay = async () => {
    if (!validateForm()) {
      setPaymentError('Please fill in all required fields before paying.');
      return false;
    }

    try {
      const state = location.state || {};
      const res = await fetch(apiUrl('/api/payment/stripe/create-payment-intent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId,
          items: (cart.items || []).map((i) => ({
            id: i.id,
            name: i.name,
            variant: i.variant,
            quantity: i.quantity,
          })),
          customerInfo,
          deliveryType: state.deliveryOption === 'local' ? 'local' : 'pickup',
          postcode: state.pinCode,
          deliveryDetails: {
            pinCode: state.pinCode,
            deliveryArea: state.deliveryArea,
            selectedDate: state.selectedDate,
            selectedTimeSlot: state.selectedTimeSlot,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setPaymentError(data.message || 'Could not update your order details.');
        return false;
      }
      setServerPricing(data.data.pricing);
      setPaymentError('');
      return true;
    } catch {
      setPaymentError('Could not reach the payment service. Please try again.');
      return false;
    }
  };

  /** Stripe reported a successful charge - turn it into an order. */
  const handlePaid = async (paymentIntent) => {
    setIsProcessing(true);
    const state = location.state || {};

    try {
      const res = await fetch(apiUrl('/api/payment/stripe/confirm'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          orderData: {
            customerInfo,
            products: (cart.items || []).map((i) => ({
              id: i.id,
              name: i.name,
              variant: i.variant,
              quantity: i.quantity,
              price: i.price,
              image: i.image,
            })),
            deliveryType: state.deliveryOption === 'local' ? 'local' : 'pickup',
            deliveryDetails: {
              pinCode: state.pinCode,
              deliveryArea: state.deliveryArea,
              selectedDate: state.selectedDate,
              selectedTimeSlot: state.selectedTimeSlot,
            },
          },
        }),
      });
      const data = await res.json();

      // The payment succeeded either way; if our own confirm call fails the
      // webhook still creates the order, so never show the shopper a failure.
      const confirmedOrderId = data.success ? data.data.orderId : paymentIntent.id;

      trackPurchase({
        orderId: confirmedOrderId,
        total: getTotal(),
        shipping: getDeliveryCharge(),
        items: cart.items || [],
      });

      setOrderPlaced(true);
      setOrderId(confirmedOrderId);
      setShowSuccessModal(true);
      clearCart();
    } catch {
      setOrderPlaced(true);
      setOrderId(paymentIntent.id);
      setShowSuccessModal(true);
      clearCart();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate("/myorders", { state: { orderSuccess: true, orderId } });
  };

  const handleCopyOrderId = () => {
    if (!orderId) return;
    navigator.clipboard.writeText(orderId)
      .then(() => {
        setCopyPopupVisible(true);
        setTimeout(() => setCopyPopupVisible(false), 1500);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        alert("Failed to copy order ID.");
      });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatTimeSlot = (slot) => {
    const slots = {
      "12-2": "12:00 PM - 2:00 PM", "2-4": "2:00 PM - 4:00 PM",
      "4-6": "4:00 PM - 6:00 PM", "6-8": "6:00 PM - 8:00 PM"
    };
    return slots[slot] || slot;
  };

  if (isLoadingUserData) {
    return (
      <>
        <Navbar />
        <div className="checkoutMainContainer">
          <div className="checkoutContainer">
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#391B14" }}>
              <p style={{ fontSize: "18px" }}>Loading checkout...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="checkoutMainContainer">
        <div className="checkoutContainer">
          <h2 className="checkoutTitle">CHECKOUT</h2>
          <div className="checkoutContent">
            <div className="checkoutLeft">
              <div className="customerInfoSection">
                <h3>Customer Information</h3>
                <form className="customerForm">
                  <div className="inputGroup">
                    <label htmlFor="name">Full Name *</label>
                    <input type="text" id="name" name="name" value={customerInfo.name} onChange={handleInputChange} required disabled={isProcessing}/>
                  </div>

                  <div className="inputGroup">
                    <label htmlFor="email">Email Address *</label>
                    <input type="email" id="email" name="email" value={customerInfo.email} onChange={handleInputChange} required disabled={isProcessing}/>
                  </div>

                  <div className="inputGroup">
                    <label htmlFor="phone">Phone Number *</label>
                    <input type="tel" id="phone" name="phone" value={customerInfo.phone} onChange={handleInputChange} required disabled={isProcessing}/>
                  </div>

                  <div className="inputGroup">
                    <label htmlFor="address">Address *</label>
                    <textarea id="address" name="address" value={customerInfo.address} onChange={handleInputChange} rows="4" required disabled={isProcessing}/>
                  </div>
                </form>
              </div>
            </div>

            <div className="checkoutRight">
              <div className="orderSummarySection">
                <h3>Order Summary</h3>

                <div className="orderItems">
                  {cart.items && cart.items.map((item) => (
                    <div key={`${item.id}_${item.variant}`} className="orderItem">
                      <div className="itemImageSmall">
                        <img src={item.image} alt={item.name} />
                      </div>
                      <div className="itemDetailsSmall">
                        <h4>{item.name}</h4>
                        <p className="itemVariant">{item.variant} × {item.quantity}</p>
                        <p className="itemTotal">£{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="deliveryDetails">
                  <h4>Delivery Details</h4>
                  <div className="deliveryInfo">
                    <p><strong>Type:</strong> {deliveryOption === "local" ? "Home Delivery" : "Store Pickup"}</p>
                    <p><strong>Date:</strong> {formatDate(selectedDate)}</p>
                    <p><strong>Time:</strong> {formatTimeSlot(selectedTimeSlot)}</p>
                  </div>
                </div>

                <div className="priceBreakdown">
                  <div className="priceRow"><span>Subtotal:</span><span style={{fontFamily : 'Inter'}}>£{getSubtotal().toFixed(2)}</span></div>
                  {deliveryOption === "local" && (
                    <div className="priceRow"><span>Delivery Charge:</span><span style={{fontFamily : 'Inter'}}>£{getDeliveryCharge().toFixed(2)}</span></div>
                  )}
                  {deliveryOption === "pickup" && (
                    <div className="priceRow"><span>Store Pickup:</span><span style={{fontFamily : 'Inter', color: '#22a86e'}}>Free</span></div>
                  )}
                  <div className="priceRow total"><span><strong>Total:</strong></span><span style={{fontFamily : 'Inter'}}><strong>£{getTotal().toFixed(2)}</strong></span></div>
                </div>

                <div className="paymentSection">
                  <h4 className="paymentSectionTitle">Payment</h4>

                  {paymentError && <p className="stripePayError">{paymentError}</p>}

                  {clientSecret ? (
                    <Elements
                      stripe={getStripe()}
                      options={{ clientSecret, appearance: stripeAppearance }}
                    >
                      <StripePaymentForm
                        total={getTotal()}
                        disabled={!validateForm() || isProcessing}
                        onBeforePay={handleBeforePay}
                        onPaid={handlePaid}
                        onError={(err) => setPaymentError(err.message || "Payment failed")}
                        returnUrl={`${window.location.origin}/checkout`}
                      />
                    </Elements>
                  ) : (
                    !paymentError && <p className="stripePayLoading">Loading secure payment…</p>
                  )}
                </div>

                <button className="backToCartBtn" onClick={() => navigate("/cart")} disabled={isProcessing}>BACK TO CART</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="successModalOverlay">
          <div className="successModalContent">
            <button className="successModalClose" onClick={handleCloseSuccessModal}><X size={24} /></button>
            <div className="successModalHeader">
              <img src="/logoBrown.png" alt="Logo" className="successLogo" />
              <span className="successBrandText">Elaichi & Love</span>
            </div>

            <div className="successCheckIcon"><CheckCircle size={80} strokeWidth={2.5} /></div>
            <h2 className="successTitle">Order Confirmed!</h2>

            <div className="successOrderId">
              Order ID: <strong>{orderId}</strong>
              <button className="copyOrderIdBtn" onClick={handleCopyOrderId}>
                {copyPopupVisible ? <CopyCheck size={18} color="#22c55e" /> : <Copy size={18} />}
              </button>
            </div>

            <div className="successMessage">
              <p>Payment successful!</p>
              <p>You will receive a confirmation email shortly.</p>
              <p>Track it any time with this Order ID and the email above, under <strong>Track Your Order</strong>.</p>
            </div>

            <button className="successCloseBtn" onClick={handleCloseSuccessModal}>Track My Order</button>
          </div>
        </div>
      )}

    </>
  );
}

export default Checkout;
